/**
 * @fileoverview Core Data Service (React port)
 * @module services/data
 * @description
 * Provides centralized access to the localStorage-backed data client and a
 * snapshot of authentication state. Ported from the Angular `DataService`; the
 * Angular signals are replaced with plain in-memory state kept in sync via the
 * client's `onAuthStateChange` listener so the public method surface
 * (`client`, `getCurrentUserId`, `getCurrentUserEmail`, `isUserAuthenticated`,
 * `refreshSession`) is preserved for consumers such as the audit-log and
 * base-data services.
 *
 * Reactive UI state (the Angular `currentUser`/`isAuthenticated` signals) is
 * handled by the auth store in a later phase; this module only exposes the
 * imperative surface other ported services depend on.
 *
 * @author Liraz Amir
 */

import { localClient, type LocalClient } from '@/data/local-client';
import type { AuthUser, LocalSession } from '@/data/local-client.types';
// LocalSession is referenced in the refreshSession return type below.

class DataService {
  private localClient: LocalClient;
  private currentUser: AuthUser | null = null;
  private authenticated = false;

  constructor() {
    // Reference the single shared client (see src/data/local-client.ts) rather
    // than creating a separate one; data.store.ts uses the same instance.
    this.localClient = localClient;
    this.initializeAuthListener();
    void this.loadSession();
  }

  /**
   * Get the local data client instance
   */
  public get client(): LocalClient {
    return this.localClient;
  }

  /**
   * Get current user ID
   */
  public getCurrentUserId(): string | null {
    return this.currentUser?.id ?? null;
  }

  /**
   * Get current user email
   */
  public getCurrentUserEmail(): string | null {
    return this.currentUser?.email ?? null;
  }

  /**
   * Check if user is authenticated
   */
  public isUserAuthenticated(): boolean {
    return this.authenticated;
  }

  /**
   * Refresh the current session
   */
  public async refreshSession(): Promise<{ session: LocalSession | null; error: unknown }> {
    const { data, error } = await this.localClient.auth.refreshSession();
    return { session: data?.session ?? null, error };
  }

  private initializeAuthListener(): void {
    this.localClient.auth.onAuthStateChange((_event, session) => {
      this.currentUser = session?.user ?? null;
      this.authenticated = !!session;
    });
  }

  private async loadSession(): Promise<void> {
    try {
      const {
        data: { session },
      } = await this.localClient.auth.getSession();
      this.currentUser = session?.user ?? null;
      this.authenticated = !!session;
    } catch (error) {
      console.error('Unexpected error loading session:', error);
      this.currentUser = null;
      this.authenticated = false;
    }
  }
}

export const dataService = new DataService();
export type { DataService };
